import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import './zoomable.css';
import {detectSignificantSizeChange} from './detectSignificantSizeChange';
import {useTranslation} from 'react-i18next';

/**
 *  This component makes its content zoomable and pan-able.
 *  It supports zooming with ctrl/meta + wheel, double click,
 *  +/- buttons and two finger pinching.
 *
 *  Use it like so : `<Zoomable> Your content </Zoomable>`
 *
 *  The component expects its content to not change of size, to
 *  take 100% of the width and automatically determine their height.
 *
 *  It should behave well when inserted in a document with more
 *  content above and below. It does not block the scroll of the
 *  page on desktop (requires wheel+ctrl to zoom) nor on mobile
 *  (when panning is not possible, touchmove events are not
 *  stopped with preventDefault).
 *
 */
export function Zoomable({children, zoomOnDoubleClick=true}) {
    const containerRef = useRef(null);
    const messageTimeoutRef = useRef(null);
    const ctrlMessage = useRef(null);
    const {t} = useTranslation();


    // We use refs for event handlers to always access the latest state without re-attaching listeners
    const state = useRef({
        // Panning and zooming of the content
        x:0,
        y:0,
        k:1,

        // Track dragging action (with mouse or single finger)
        isDragging: false,
        wasDraggedAround: false,
        startX: 0,
        startY: 0,
        startTx: 0,
        startTy: 0,

        // Track pinching action (with 2 fingers)
        isPinching: false,
        startDist: 0,
        startK: 1,
        startCenter: {x: 0, y: 0},
        startTrans: {x: 0, y: 0, k: 1},

        // Minimum zoom, this is to avoid the user zooming out so much that the
        // viewport is larger than the whole object. Updated once we measure the
        // content.
        minK:1,
        // Limits the panning to avoid showing what's "around" the content. Updated once we measure the
        // content.
        maxPanX:0,
        maxPanY:0,
        containerSize:{
            width:0,
            height:0
        }
    }).current;

    function updateContentStyle() {
        contenRef.current.style.transform= `translate(${state.x}px, ${state.y}px) scale(${state.k})`;
        contenRef.current.style.cursor=  state.isDragging ? 'grabbing':'grab';
    }

    const setTransform= React.useCallback((transformFunction) => {

        const {x, y, k, maxPanX, maxPanY, containerSize, minK} = state;
        const nt=transformFunction({x, y, k});
        if(nt.k<minK) {
            nt.k=minK;
        }
        if(nt.x>0) {
            nt.x=0;
        }
        if(nt.y>0) {
            nt.y=0;
        }
        const minX = Math.min(0, -maxPanX*nt.k+containerSize.width);
        const minY = Math.min(0, -maxPanY*nt.k+containerSize.height);
        if(nt.x<minX) {
            nt.x=minX;
        }
        if(nt.y<minY) {
            nt.y=minY;
        }
        const changed=x!==nt.x || y!==nt.y || k!==nt.k;
        if(changed) {
            state.x=nt.x;
            state.y=nt.y;
            state.k=nt.k;
            updateContentStyle();
            state.wasDraggedAround = true;
        }
        return changed;
    });

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const onWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                try{
                    window.sessionStorage.setItem('throne_zoomable_user_got_it', 'yes');
                }catch {
                    console.warn('Session storing blocked');
                }
                const rect = container.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;

                const delta = -e.deltaY;
                // Scale factor
                const factor = Math.pow(1.001, delta);

                setTransform((prev) => {
                    let newK = prev.k * factor;
                    // Clamp zoom
                    if (newK < 0.1) newK = 0.1;
                    if (newK > 50) newK = 50;

                    // Zoom towards mouse position
                    const newX = mx - (mx - prev.x) * (newK / prev.k);
                    const newY = my - (my - prev.y) * (newK / prev.k);

                    return {x: newX, y: newY, k: newK};
                });

                if(ctrlMessage.current) ctrlMessage.current.style.opacity=0;
            } else {
                try{
                    if(window.sessionStorage.getItem('throne_zoomable_user_got_it')) {
                        return;
                    }
                }catch {
                    console.warn('Session reading blocked');
                }
                if(ctrlMessage.current) ctrlMessage.current.style.opacity=0.8;
                if (messageTimeoutRef.current) {
                    clearTimeout(messageTimeoutRef.current);
                }
                messageTimeoutRef.current = setTimeout(
                    () => {
                        if(ctrlMessage.current)ctrlMessage.current.style.opacity = 0;
                    },
                    2000,
                );
            }
        };

        // Non-passive listener to allow preventDefault
        container.addEventListener('wheel', onWheel, {passive: false});
        return () => container.removeEventListener('wheel', onWheel);
    }, []);

    const onMouseDown = (e) => {
        state.isDragging = true;
        state.wasDraggedAround = false;
        state.startX = e.clientX;
        state.startY = e.clientY;
        state.startTx = state.x;
        state.startTy = state.y;
        e.preventDefault();
        updateContentStyle();
    };

    useEffect(() => {
        const onMouseUp = (e) => {
            if (!state.isDragging) return;
            if(state.wasDraggedAround ) {
                e.preventDefault();
                state.suppressNextClickEvent=true;
            }
            state.isDragging = false;
            updateContentStyle();
        };
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, []);


    useEffect(() => {
        const onMouseMove = (e) => {
            if (!state.isDragging) return;
            e.preventDefault();
            const dx = e.clientX - state.startX;
            const dy = e.clientY - state.startY;
            setTransform((prev) => ({
                ...prev,
                x: state.startTx + dx,
                y: state.startTy + dy,
            }));
        };

        window.addEventListener('mousemove', onMouseMove);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
        };
    }, []);


    useEffect(() => {
        const suppressClick = (e) => {
            if(!state.suppressNextClickEvent) return;
            state.suppressNextClickEvent=false;
            e.preventDefault();
            e.stopPropagation();
        };
        window.addEventListener('click', suppressClick, {capture:true});
        return () => {
            window.removeEventListener('click', suppressClick, {capture:true});
        };
    }, []);



    const onTouchStart = (e) => {
        if (e.touches.length === 1) {
            state.isDragging = true;
            state.wasDraggedAround = false;
            state.isPinching = false;
            state.startX = e.touches[0].clientX;
            state.startY = e.touches[0].clientY;
            state.startTx = state.x;
            state.startTy = state.y;
        } else if (e.touches.length === 2) {
            state.isDragging = false;
            state.isPinching = true;

            const t1 = e.touches[0];
            const t2 = e.touches[1];

            const dist = Math.hypot(
                t1.clientX - t2.clientX,
                t1.clientY - t2.clientY,
            );
            const cx = (t1.clientX + t2.clientX) / 2;
            const cy = (t1.clientY + t2.clientY) / 2;

            const rect = containerRef.current.getBoundingClientRect();

            state.startDist = dist;
            state.startK = state.k;
            state.startCenter = {x: cx - rect.left, y: cy - rect.top};
            state.startTrans = {x:state.x, y:state.y, k:state.k};
        }
    };
    React.useEffect(() => {
        const onTouchMove = (e) => {
            if (state.isDragging && e.touches.length === 1) {
                const dx = e.touches[0].clientX - state.startX;
                const dy = e.touches[0].clientY - state.startY;
                const changed=setTransform((prev) => ({
                    ...prev,
                    x: state.startTx + dx,
                    y: state.startTy + dy,
                }));
                if (e.cancelable && changed) e.preventDefault();
            } else if (state.isPinching && e.touches.length === 2) {
                if (e.cancelable) e.preventDefault();

                const t1 = e.touches[0];
                const t2 = e.touches[1];

                const dist = Math.hypot(
                    t1.clientX - t2.clientX,
                    t1.clientY - t2.clientY,
                );
                const cx = (t1.clientX + t2.clientX) / 2;
                const cy = (t1.clientY + t2.clientY) / 2;

                const rect = containerRef.current.getBoundingClientRect();
                const relCx = cx - rect.left;
                const relCy = cy - rect.top;

                const scaleFactor = dist / state.startDist;
                let newK = state.startK * scaleFactor;
                if (newK < 0.1) newK = 0.1;
                if (newK > 50) newK = 50;

                const sT = state.startTrans;
                const sC = state.startCenter;

                // Zoom relative to the initial pinch center
                const px = (sC.x - sT.x) / sT.k;
                const py = (sC.y - sT.y) / sT.k;

                const newX = relCx - px * newK;
                const newY = relCy - py * newK;

                setTransform(() => ({x: newX, y: newY, k: newK}));
            }
        };
        // We need to explicitly mark the event as active to allow cancellation.
        containerRef.current.addEventListener('touchmove', onTouchMove, {passive: false});
        return () => {
            containerRef.current.removeEventListener('touchmove', onTouchMove, {passive: false});
        };
    });


    const onTouchEnd = (e) => {
        if(state.isDragging && state.wasDraggedAround ) {
            e.preventDefault();
        }
        state.isPinching = false;
        state.isDragging = false;

        if (e.touches.length === 1) {
            // Switch to single finger panning
            state.isDragging = true;
            state.startX = e.touches[0].clientX;
            state.startY = e.touches[0].clientY;
            state.startTx = state.x;
            state.startTy = state.y;
        }
    };

    const handleZoom = (zoomIn) => {
        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const cx = rect.width / 2;
        const cy = rect.height / 2;

        const factor = zoomIn ? 1.2 : 1 / 1.2;

        setTransform((prev) => {
            let newK = prev.k * factor;
            if (newK < 0.1) newK = 0.1;
            if (newK > 50) newK = 50;

            const newX = cx - (cx - prev.x) * (newK / prev.k);
            const newY = cy - (cy - prev.y) * (newK / prev.k);

            return {x: newX, y: newY, k: newK};
        });
    };
    const handleDoubleClick = (e) => {
        if(!zoomOnDoubleClick) {
            return;
        }
        handleZoom(true);
        e.preventDefault();
    };

    const contenRef = React.useRef();
    const cleanupWatcher = React.useRef();

    const onContentRef = (node) => {
        if(contenRef.current ||!node) return;
        contenRef.current=node;
        updateContentStyle();
        const resetZoomAndMeasureContentSize = () => {
            // Make content visible to the user.
            contenRef.current.style.opacity= 1;
            // Reset the scale and position of the content before measuring it.
            setTransform(() => ({x:0, y:0, k:1}));
            const contentSize = contenRef.current.getBoundingClientRect();
            containerRef.current.style.minHeight = contentSize.height+'px';
            const containerSize=containerRef.current.getBoundingClientRect();
            state.minK=Math.min(containerSize.width/contentSize.width, 1);
            state.maxPanX=contentSize.width;
            state.maxPanY=contentSize.height;
            state.containerSize=containerSize;
        };
        setTimeout(resetZoomAndMeasureContentSize);
        cleanupWatcher.current=detectSignificantSizeChange(contenRef.current, resetZoomAndMeasureContentSize);
    };
    React.useEffect(() => () => cleanupWatcher.current?.(), []);

    return (
        <div
            className="zoomable-container"
            ref={containerRef}
            style={{
                position: 'relative',
                overflow: 'hidden',
                touchAction: 'pan-x pan-y',
            }}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            <div
                onDoubleClick={handleDoubleClick}
                ref={onContentRef}
                style={{
                    transformOrigin: '0 0',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    minWidth: '100%',
                    opacity:0
                }}
            >
                {children}
            </div>

            <button
                className="throne-btn-zoom-in"
                onClick={() => handleZoom(true)}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
            />
            <button
                className="throne-btn-zoom-out"
                onClick={() => handleZoom(false)}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
            />
            <div
                ref={ctrlMessage} className="requireCtrlOrMetaKeyToZoomWarning" style={{opacity: 0}}>
                {t('requireCtrlOrMetaKeyToZoomWarning')}
            </div>

        </div>
    );
}

Zoomable.propTypes = {
    /** Main content to zoom onto. */
    children: PropTypes.node,
    /** Set it to `false` to ignore double clicks. Useful if the content
     * uses that event (like the zone editor does). */
    zoomOnDoubleClick: PropTypes.bool,
};